<template>
  <div class="app">
    <NuxtLayout class="app__layout">
      <NuxtPage class="app__template" />
    </NuxtLayout>

    <ThirdpartyLoader :loadtime="7500" />

    <LazyDevtools />
  </div>
</template>

<script lang="ts" setup>
// Added this to handle removal of utm_* parameters by varnish
// see https://github.com/nuxt/nuxt/issues/23153
await useAsyncData('data',
  async (nuxtApp) => {
    delete nuxtApp?.payload?.path
    return {}
  },
)

const { public: config } = useRuntimeConfig()
const { t } = useI18n({ useScope: 'global' })

const { query } = useRoute()
const { locale } = useI18n()

useHead({
  title: '',
  titleTemplate: '%s | ' + t('nuxtSiteConfig.name'),
  htmlAttrs: {
    lang: locale.value,
  },
  meta: [
    { name: 'description', content: '' },
    { name: 'format-detection', content: 'telephone=no' },
    { name: 'msapplication-TileColor', content: '#16002b' },
    { name: 'theme-color', content: '#ffffff' },
  ],
  link: [
    { rel: 'icon', type: 'image/png', sizes: '96x96', href: '/favicon-96x96.png' },
    { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
    { rel: 'shortcut icon', href: '/favicon.ico' },
    { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
    { rel: 'manifest', href: '/site.webmanifest' },
    { rel: 'preconnect', href: config.xcdnUrl },
  ],
})

/* Disable thirdparty scripts/widgets to test application performnce in for example Google Lighthouse */
if (query.thirdparty !== 'disabled' && config?.gtm?.id) {
  useServerHead({
    noscript: [
      {
        tagPosition: 'bodyOpen',
        innerHTML: `<iframe src="${config?.gtm?.source?.replace('/gtm.js', '/ns.html') || 'https://www.googletagmanager.com/ns.html'}?id=${config.gtm.id}"
height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
      },
    ],
    script: [
      {
        tagPriority: 101,
        innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.defer=${config?.gtm?.defer || true};j.src=
'${config?.gtm?.source || 'https://www.googletagmanager.com/gtm.js'}?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${config.gtm.id}');`,
      },
      // {
      //   tagPriority: 101,
      //   innerHTML: `window.dataLayer=(window.dataLayer||[]);window.dataLayer.push({'event':'gtm.js','gtm.start': new Date().getTime(),});`,
      // },
      // {
      //   tagPriority: 101,
      //   src: `${config?.gtm?.source || 'https://www.googletagmanager.com/gtm.js'}?id=${config.gtm.id}`,
      //   defer: config?.gtm?.defer || true,
      // },
    ],
  })
}

const { fetchStoreData } = useStore()
await fetchStoreData()
useCart()
useTweakwise()
</script>

<style>
@import url('~layers/app/assets/css/vars.css');
@import url('~layers/app/assets/css/tokens/colors.css');
@import url('~layers/app/assets/css/base/reset.css');
@import url('~layers/app/assets/css/base/root-and-sectioning.css');
@import url('~layers/app/assets/css/base/forms-and-inputs.css');
@import url('~layers/app/assets/css/base/forms-and-inputs__file.css');
@import url('~layers/app/assets/css/base/forms-and-inputs__color.css');
@import url('~layers/app/assets/css/base/forms-and-inputs__range.css');
@import url('~layers/app/assets/css/base/forms-and-inputs__select.css');
@import url('~layers/app/assets/css/base/forms-and-inputs__checkbox-radio.css');
@import url('~layers/app/assets/css/base/text-content.css');
@import url('~layers/app/assets/css/base/text-content__headings.css');
@import url('~layers/app/assets/css/base/text-content__anchor-button.css');
@import url('~layers/app/assets/css/base/text-content__kbd.css');
@import url('~layers/app/assets/css/base/text-content__ruby.css');
@import url('~layers/app/assets/css/base/multimedia-and-embeds.css');
@import url('~layers/app/assets/css/base/lists.css');
@import url('~layers/app/assets/css/base/tables.css');
@import url('~layers/app/assets/css/base/details-summary.css');
@import url('~layers/app/assets/css/base/dialog-popover-backdrop.css');
@import url('~layers/app/assets/css/base/meter.css');
@import url('~layers/app/assets/css/base/progress.css');
@import url('~layers/app/assets/css/styleguide/_difference.css');
@import url('~layers/app/assets/css/utilities/utilities.css');
@import url('~layers/app/assets/css/utilities/buttons.css');
@import url('~layers/app/assets/css/utilities/messages.css');
@import url('~layers/app/assets/css/utilities/tables.css');
@import url('~layers/app/assets/css/vue-formkit.css');
@import url('~layers/app/assets/css/third-parties.css');
@import url('~layers/app/app.css');
</style>
